import {
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	ElementRef,
	HostBinding,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { FilterCriteria, SortOptionItem } from '@woolworthsnz/trader-api';
import {
	NewLayoutOfProductCategoriesAndFiltersVariants,
	NewLayoutOfProductCategoriesAndFiltersExp2Variants,
} from '@woolworthsnz/styleguide';
import { SearchSorterComponent } from '../search-sorter/search-sorter.component';
import { SearchToggleFilterComponent } from '../search-toggle-filter/search-toggle-filter.component';
import { SearchFilterComponent } from '../search-filter/search-filter.component';
import { SearchSingleSelectComponent, Option } from '../search-single-select/search-single-select.component';

interface FilterChangeArgs {
	filter: FilterCriteria;
	isSelected: boolean;
	url: UrlSegment[];
}

export interface DasFilterChangeArgs {
	dasFilter: FilterCriteria;
	isSelected: boolean;
	url: UrlSegment[];
}
enum DefaultVariation {
	default = 'default',
}

type Variant =
	| NewLayoutOfProductCategoriesAndFiltersVariants.V3
	| NewLayoutOfProductCategoriesAndFiltersExp2Variants.Control
	| NewLayoutOfProductCategoriesAndFiltersExp2Variants.V1
	| NewLayoutOfProductCategoriesAndFiltersExp2Variants.V2
	| DefaultVariation.default;

export const SPECIALS_GROUP = 'Specials';
export const FILTER_OPTION_ALL_KEY = 'All';
const SPECIALS_VARIANT: Variant[] = [
	NewLayoutOfProductCategoriesAndFiltersExp2Variants.V1,
	NewLayoutOfProductCategoriesAndFiltersExp2Variants.V2,
	DefaultVariation.default,
];
export const DEFAULT_SINGLE_SELECT_CATEGORY_OPTION: Option = { value: 'all', text: 'All' };

@Component({
	selector: 'search-filter-combo',
	standalone: true,
	imports: [
		CommonModule,
		NgFor,
		NgIf,
		SearchSorterComponent,
		SearchToggleFilterComponent,
		SearchFilterComponent,
		SearchSingleSelectComponent,
	],
	templateUrl: './search-filter-combo.component.html',
	styleUrls: ['./search-filter-combo.component.scss'],
})
export class SearchFilterComboComponent implements OnChanges {
	@Input() selectedFilters: Array<FilterCriteria> = [];
	@Input() selectedCategories: Array<FilterCriteria> = [];
	@Input() sortOptions: Array<SortOptionItem> = [];
	@Input() currentSortOption = '';
	@Input() categories: Array<FilterCriteria> = [];
	@HostBinding('attr.options-reveal-approach')
	@Input()
	optionsRevealApproach: 'dropdown' | 'modal' = 'dropdown';
	@Input() totalItems?: number;
	@Input() variant?: Variant;
	@Input() multiSelectCategories = false;

	@Output() filterChange = new EventEmitter<Array<FilterChangeArgs>>();
	@Output() sortChange = new EventEmitter<string>();
	@Output() categoryChange = new EventEmitter<Array<DasFilterChangeArgs>>();

	@ViewChild('filterGroupsContainer') filterGroupsContainer: ElementRef<HTMLElement>;

	private _filterGroups: Array<FilterCriteria[]> = [];
	private _specialsGroupFilterOptions: Array<FilterChangeArgs> = [];
	private _otherFilterGroups: Array<FilterCriteria[]> = [];

	constructor(private route: ActivatedRoute) {}

	get filterGroups(): Array<FilterCriteria[]> {
		return this._otherFilterGroups;
	}

	get specialsGroupFilterOptions(): Array<FilterChangeArgs> {
		return this._specialsGroupFilterOptions;
	}

	get categoryOptions(): Array<Option> {
		return [
			DEFAULT_SINGLE_SELECT_CATEGORY_OPTION,
			...this.categories.map(({ value = '', name = '' }) => ({
				value,
				text: name,
			})),
		];
	}

	get currentSingleSelectCategoryOptionValue(): string {
		return this.selectedCategories[0]?.value || DEFAULT_SINGLE_SELECT_CATEGORY_OPTION.value;
	}

	private get url(): UrlSegment[] {
		return this.route.snapshot?.url;
	}

	@Input()
	set filterGroups(value: Array<FilterCriteria[]>) {
		this._filterGroups = value;
	}

	trackByOptionNameFn = (_: number, option: FilterChangeArgs): string | undefined => option.filter.name;
	trackByGroupFn = (index: number): number => index;

	onFilterChange(filterOptions: Array<FilterChangeArgs>): void {
		this.filterChange.emit(filterOptions);
		this.resetFiltersPosition();
	}

	onCategoryChange(filterOptions: Array<FilterChangeArgs>): void {
		const dasFilterOptions = filterOptions.map(({ filter, ...rest }) => ({
			dasFilter: filter,
			...rest,
		}));

		this.categoryChange.emit(dasFilterOptions);
		this.resetFiltersPosition();
	}

	onSingleCategoryChange(optionValue: string | number | boolean): void {
		const categoriesFilterOptions = this.convertFilterGroupToFilterOptions(this.categories);
		const checkedCategoriesFilterOptions = categoriesFilterOptions.map(({ isSelected: _, filter, ...rest }) => ({
			filter,
			isSelected: filter.value === optionValue,
			...rest,
		}));
		this.onCategoryChange(checkedCategoriesFilterOptions);
	}

	onSortChange(sortOption: string): void {
		this.sortChange.emit(sortOption);
		this.resetFiltersPosition();
	}

	onFilterToggle(option: FilterChangeArgs): void {
		this.filterChange.emit([option]);
		this.resetFiltersPosition();
	}

	convertFilterGroupToFilterOptions(group: Array<FilterCriteria>): Array<FilterChangeArgs> {
		return group.map((filter) => ({
			filter,
			isSelected: this.isFilterSelected(filter),
			url: this.url,
		}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.hasOwnProperty('variant') || changes.hasOwnProperty('filterGroups')) {
			this.updateFilterOptionsByVariant();
		}
	}

	private updateFilterOptionsByVariant(): void {
		if (this.variant) {
			if (SPECIALS_VARIANT.includes(this.variant)) {
				const specialsFilterGroup = this._filterGroups.find((group) => group[0].group === SPECIALS_GROUP) || [];
				const individualSpecialFilters = specialsFilterGroup.filter(
					(option) => option.key !== FILTER_OPTION_ALL_KEY
				);
				const theAllSpecialFilter = specialsFilterGroup
					.filter((option) => option.key === FILTER_OPTION_ALL_KEY)
					.map(({ name: _, ...rest }) => ({ name: 'Specials & offers only', ...rest }) as FilterCriteria);
				const specialsVariantFilterGroup: {
					[key in Variant]?: FilterCriteria[];
				} = {
					[NewLayoutOfProductCategoriesAndFiltersExp2Variants.V2]: individualSpecialFilters,
					[NewLayoutOfProductCategoriesAndFiltersExp2Variants.V1]: theAllSpecialFilter,
					[DefaultVariation.default]: individualSpecialFilters,
				};

				this._specialsGroupFilterOptions = this.convertFilterGroupToFilterOptions(
					specialsVariantFilterGroup[this.variant] || []
				);
				this._otherFilterGroups = this._filterGroups.filter((group) => group[0].group !== SPECIALS_GROUP);
			} else {
				this._otherFilterGroups = this._filterGroups;
			}
		}
	}

	private isFilterSelected(filter: FilterCriteria): boolean {
		return (
			this.selectedFilters.some(({ key, value }) => key === filter.key && value === filter.value) ||
			this.selectedCategories.some(({ key, value }) => key === filter.key && value === filter.value)
		);
	}

	private resetFiltersPosition(): void {
		setTimeout(() => {
			this.filterGroupsContainer.nativeElement.scrollLeft = 0;
		}, 0);
	}
}
